import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, CircularProgress, MenuItem } from '@mui/material';
import { TextField, CustomCheckbox, CustomButton, CustomSelect } from '../../../../v2/MUI_custom_component';
import FileUploadWrapper from '../../../../v2/Utils/FileUpload';
import { getAge, calculateAgesAndFlags } from '../utils/validation';
import ReusableModal from '../../../../v2/MUI_custom_component/Modal/CustomModal';
import CustomFileUploadInput from '../../../Beneficiary/StaffInsurance/Components/FileUpload/CustomFileUploadInput';
import CustomChip from '../../../Components/CustomChip';
import { FirmOwnershipType } from '../../../../v2/Benefits/Beneficiary/constants';
import AgeWarningModal from '../../../../v2/Benefits/Beneficiary/Components/AgeWarningModal';
import { getUpdatedReationAction } from '../../../../v2/Benefits/Beneficiary/APIs/action';
import { toast } from 'react-toastify';

const ReusableFileUploadArea = ({ 
    // Core functionality
    onFileUpload, 
    onFileDelete,
    isLoading, 
    error, 
    uploadedFile, 
    extractedData, 
    
    // Form data
    phoneNumber, 
    onPhoneChange, 
    relation,
    onRelationChange,
    
    // Document type selection
    showDocumentTypeSelection = false,
    selectedDocumentType = '',
    onDocumentTypeChange,
    relationData = '',
    
    // Configuration
    relations = [],
    uploadTitle = "Upload Document",
    uploadedTitle = "Uploaded Document",
    processingText = "Processing Document...",
    acceptedFileTypes = ['.jpg', '.jpeg', '.png', '.pdf'],
    
    // Validation options
    requirePhone = true,
    requireRelation = false,
    minAge = 18,
    maxAge = 60,
    context = 'beneficiary',
    
    // Callbacks
    onConfirm, 
    onEditManually,
    
    // UI customization
    showConfirmModal = true,
    confirmModalTitle = "Confirm Details",
    saveButtonText = "Save & Continue",
    editManuallyText = "No, Fill Manually",
    confirmText = "Yes",
    confirmationCheckboxText = "Details provided herein are true and I have obtained all relevant organisational consents for the purposes.",
    
    // Additional fields and customization
    additionalFields = [],
    customValidation,
    onFieldChange,
    
    // Age warning modal props
    owner_type_text,
    onRelationUpdate
}) => {
    const [confirm, setConfirm] = useState(false);
    const [phoneValidation, setPhoneValidation] = useState('');
    const [relationValidation, setRelationValidation] = useState('');
    const [consentValidation, setConsentValidation] = useState('');
    const [ageValidation, setAgeValidation] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [openConfirmModal, setOpenConfirmModal] = useState(false);
    const [additionalFieldsData, setAdditionalFieldsData] = useState({});
    const [additionalFieldsValidation, setAdditionalFieldsValidation] = useState({});
    const [showAgeModal, setShowAgeModal] = useState(false);
    const [updateRelation, setUpdateRelation] = useState(false);

    // Document types configuration
    const documentTypes = [
        { label: 'Aadhar', value: 'AADHAAR' },
        { label: 'Pan', value: 'PAN' }
    ];

    // Get the first available document type based on relation
    const getDefaultDocumentType = () => {
        if (relationData?.toLowerCase() === 'self') {
            return 'PAN'; // Only PAN is available for self
        }
        return 'AADHAAR'; // AADHAAR is first option for non-self relations
    };

    // Set default document type if none is selected or when relation changes
    React.useEffect(() => {
        if (showDocumentTypeSelection && onDocumentTypeChange) {
            const defaultType = getDefaultDocumentType();
            // Set default if no selection or if current selection is AADHAAR but relation is 'self'
            if (!selectedDocumentType || 
                (selectedDocumentType === 'AADHAAR' && relationData?.toLowerCase() === 'self')) {
                onDocumentTypeChange(defaultType);
            }
        }
    }, [showDocumentTypeSelection, relationData, onDocumentTypeChange]);

    const handleUpload = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            onFileUpload(files[0]);
        }
    };

    const handlePhoneChange = (value) => {
        onPhoneChange(value);
        
        if (value.trim()) {
            const cleanedValue = value.replace(/\D/g, '');
            if (cleanedValue.length === 10) {
                setPhoneValidation('');
            } else if (cleanedValue.length > 10) {
                setPhoneValidation('Phone number cannot exceed 10 digits');
            }
        } else {
            setPhoneValidation('');
        }

        if (onFieldChange) {
            onFieldChange('phone_number', value);
        }
    };

    const handlePhoneBlur = () => {
        if (requirePhone) {
            if (!phoneNumber.trim()) {
                setPhoneValidation('Phone number is required');
            } else if (!/^\d{10}$/.test(phoneNumber.replace(/\D/g, ''))) {
                setPhoneValidation('Please enter a valid 10-digit phone number');
            } else {
                setPhoneValidation('');
            }
        }
    };

    const handleAdditionalFieldChange = (fieldName, value) => {
        setAdditionalFieldsData(prev => ({
            ...prev,
            [fieldName]: value
        }));

        // Clear validation error for this field
        if (additionalFieldsValidation[fieldName]) {
            setAdditionalFieldsValidation(prev => {
                const { [fieldName]: removed, ...rest } = prev;
                return rest;
            });
        }

        if (onFieldChange) {
            onFieldChange(fieldName, value);
        }
    };

    // Validation function
    const validateForm = () => {
        if (customValidation) {
            const validation = customValidation({
                ...extractedData,
                phone_number: phoneNumber,
                relation,
                ...additionalFieldsData
            }, confirm);
            
            setPhoneValidation(validation.errors?.phone_number || '');
            setRelationValidation(validation.errors?.relation || '');
            setAgeValidation(validation.errors?.age || '');
            setConsentValidation(validation.errors?.confirm || '');
            
            return validation.isValid;
        }

        // Reset all validations
        setPhoneValidation('');
        setRelationValidation('');
        setConsentValidation('');
        setAgeValidation('');
        setAdditionalFieldsValidation({});

        let isValid = true;

        // Phone number validation
        if (requirePhone) {
            if (!phoneNumber.trim()) {
                setPhoneValidation('Phone number is required');
                isValid = false;
            } else if (!/^\d{10}$/.test(phoneNumber.replace(/\D/g, ''))) {
                setPhoneValidation('Please enter a valid 10-digit phone number');
                isValid = false;
            }
        }

        // Age validation
        if (extractedData.dob) {
            const ageResult = calculateAgesAndFlags([extractedData.dob], minAge, maxAge);
            if (ageResult[0]?.ageFlag) {
                const age = ageResult[0]?.age;
                if (age < minAge) {
                    setAgeValidation(`Age should be ${minAge}+`);
                    isValid = false;
                } else if (age > maxAge) {
                    setAgeValidation(`Age must be under ${maxAge} years`);
                    isValid = false;
                }
            }
        }

        // Relation validation
        if (requireRelation && !relation) {
            setRelationValidation('Please select a relation');
            isValid = false;
        }

        // Additional fields validation
        let additionalFieldErrors = {};
        additionalFields.forEach(field => {
            if (field.required && (!additionalFieldsData[field.name] || additionalFieldsData[field.name].toString().trim() === '')) {
                additionalFieldErrors[field.name] = `${field.label} is required`;
                isValid = false;
            }
        });
        setAdditionalFieldsValidation(additionalFieldErrors);

        // Consent validation
        if (!confirm) {
            setConsentValidation('Please confirm the details to proceed');
            isValid = false;
        }

        return isValid;
    };


    const handleConfirmClick = async () => {
        if (isSubmitting) {
            return;
        }
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        await onConfirm({
            ...extractedData,
            phone_number: phoneNumber,
            relation,
            ...additionalFieldsData
        });
        setOpenConfirmModal(false);
        setIsSubmitting(false);
    };

    const shouldShowConfirmButton = () => {
        return !isLoading && 
               uploadedFile && 
               extractedData && 
               Object.keys(extractedData).length > 0;
    };
    // Blood relation API call for age warning
    const callBloodRelationAPI = async (firm_type) => {
        const { error, message, data } = await getUpdatedReationAction(firm_type);
        if (!error) {
            setUpdateRelation(true);
            setShowAgeModal(false);
            if (onRelationUpdate && data && data.length > 0) {
                onRelationUpdate(data);
            }
        } else {
            setUpdateRelation(false);
            toast.error(message);
        }
    };

    const handleCloseAgeModal = () => {
        setShowAgeModal(false);
    };

    // Age validation effect
    useEffect(() => {
        if (extractedData?.dob && owner_type_text) {
            const age = getAge(extractedData.dob);
            if (
                age &&
                age > 60 &&
                owner_type_text?.value !== FirmOwnershipType.PROPRIETORSHIP &&
                owner_type_text?.value !== FirmOwnershipType.HINDU_UNDIVIDED_FAMILY
            ) {
                setShowAgeModal(true);
            }
        }
    }, [extractedData?.dob, owner_type_text]);
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Relation selector for nominees */}
            {requireRelation && onRelationChange && (
                <Grid container spacing={2} sx={{ my: 0 }}>
                    <Grid item xs={12}>
                        <CustomSelect
                            id="relation-select"
                            label="Relation*"
                            value={relation || ''}
                            onChange={(e) => {
                                onRelationChange(e.target.value);
                                if (onFieldChange) {
                                    onFieldChange('relation', e.target.value);
                                }
                            }}
                            fullWidth
                            error={!!relationValidation}
                            helperText={relationValidation}
                            disabled={relations && relations.length === 1}
                        >
                            {relations && relations.length > 0 ? (
                                relations.map((relationOption, index) => (
                                    <MenuItem key={index} value={relationOption.value || relationOption}>
                                        {relationOption.label || relationOption}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem value="" disabled>
                                    No relations available
                                </MenuItem>
                            )}
                        </CustomSelect>
                    </Grid>
                </Grid>
            )}

            {/* Document Type Selection */}
            <Grid container spacing={2}>
                <Grid item xs={6}>
                {showDocumentTypeSelection && !uploadedFile && relationData?.toLowerCase() !== 'self' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px'}}>
                        <Typography
                            variant='labelD'
                            sx={{ color: '#666'}}
                        >
                            Choose Document
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {/* Show both chips if relation is not "self", only PAN if relation is "self" */}
                            {documentTypes.map((docType) => {
                                // Only show AADHAAR chip if relation is not "self"
                                if (docType.value === 'AADHAAR' && relationData?.toLowerCase() === 'self') {
                                    return null;
                                }
                                
                                return (
                                    <CustomChip
                                        key={docType.value}
                                        label={docType.label}
                                        onClick={() => onDocumentTypeChange && onDocumentTypeChange(docType.value)}
                                        variant={selectedDocumentType === docType.value  ? "filled" : 'outlined'}
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                )}
                </Grid>
            </Grid>

            {/* File upload area */}
            {isLoading ? (
                <Box sx={{ 
                    border: '2px dashed #ddd',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    backgroundColor: '#fafafa'
                }}>
                    <CircularProgress size={48} sx={{ mb: 2 }} />
                    <Typography variant="body1" color="primary">
                        {processingText}
                    </Typography>
                </Box>
            ) : !uploadedFile ? (
                <Box>
                    <CustomFileUploadInput
                        title={uploadTitle}
                        acceptedFileTypes={acceptedFileTypes}
                        onFileSelect={onFileUpload}
                    />
                    {error && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {error}
                        </Typography>
                    )}
                </Box>
            ) : (
                <Box>
                    <Typography variant="labelD" sx={{ color: '#666' }}>
                        {uploadedTitle}
                    </Typography>
                    <FileUploadWrapper
                        handleUpload={handleUpload}
                        title={uploadTitle}
                        acceptedFileTypes={acceptedFileTypes}
                        existingFileName={uploadedFile.name}
                        existingFileUrl={URL.createObjectURL(uploadedFile)}
                        showOriginalOnDelete={false}
                        onDelete={() => {
                            if (onFileDelete) {
                                setPhoneValidation('');
                                setRelationValidation('');
                                setConsentValidation('');
                                setAgeValidation('');
                                setAdditionalFieldsValidation({});
                                onFileDelete();
                            }
                        }}
                    />
                    {shouldShowConfirmButton() && (
                        <Box sx={{ p: 1 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={6}>
                                    <Typography variant="labelD" sx={{ color: '#666' }}>
                                        Name
                                    </Typography>
                                    <Typography variant="body1">
                                        {extractedData.full_name}
                                    </Typography>
                                </Grid>
                                {selectedDocumentType !== 'PAN' && (
                                    <Grid item xs={6} sm={6}>
                                        <Typography variant="labelD" sx={{ color: '#666' }}>
                                            Gender
                                        </Typography>
                                        <Typography variant="body1">
                                            {extractedData.gender}
                                        </Typography>
                                    </Grid>
                                )}
                                
                                <Grid item xs={6} sm={6}>
                                    <Typography variant="labelD" sx={{ color: '#666' }}>
                                        Date of Birth
                                    </Typography>
                                    <Typography variant="body1">
                                        {new Date(extractedData.dob).toLocaleDateString('en-GB', { 
                                            day: 'numeric', 
                                            month: 'long', 
                                            year: 'numeric' 
                                        })}
                                    </Typography>
                                </Grid>
                                
                                {extractedData.dob && (
                                    <Grid item xs={6} sm={6}>
                                        <Typography variant="labelD" sx={{ color: '#666' }}>
                                            Age
                                        </Typography>
                                        <Typography 
                                            variant="body1"
                                            sx={{
                                                color: calculateAgesAndFlags([extractedData.dob], minAge, maxAge)[0]?.ageFlag 
                                                       ? 'error.main' : 'inherit'
                                            }}
                                        >
                                            {getAge(extractedData.dob)} years
                                            {ageValidation && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                                    {ageValidation}
                                                </Typography>
                                            )}
                                        </Typography>
                                    </Grid>
                                )}
                                
                            </Grid>
                        </Box>
                    )}
                </Box>
            )}
            {/* Additional & Phone Number Field */}
            
                <Grid container spacing={2}>
                       {additionalFields.length > 0 && shouldShowConfirmButton() && additionalFields.map((field, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            {field.type === 'text' && (
                                <TextField
                                    label={field.label}
                                    value={additionalFieldsData[field.name] || ''}
                                    onChange={(e) => handleAdditionalFieldChange(field.name, e.target.value)}
                                    placeholder={field.placeholder}
                                    required={field.required}
                                    error={!!additionalFieldsValidation[field.name]}
                                    helperText={additionalFieldsValidation[field.name]}
                                    fullWidth
                                    variant="outlined"
                                />
                            )}
                            {field.type === 'select' && (
                                <CustomSelect
                                    id={`${field.name}-select`}
                                    label={field.label}
                                    value={additionalFieldsData[field.name] || ''}
                                    onChange={(e) => handleAdditionalFieldChange(field.name, e.target.value)}
                                    required={field.required}
                                    error={!!additionalFieldsValidation[field.name]}
                                    helperText={additionalFieldsValidation[field.name]}
                                    fullWidth
                                    displayEmpty
                                >
                                    <MenuItem value="" disabled>
                                        {field.placeholder || `Choose ${field.label.toLowerCase()}`}
                                    </MenuItem>
                                    {field.options.map((option, optIndex) => (
                                        <MenuItem 
                                            key={optIndex} 
                                            value={option.value || option}
                                        >
                                            {option.label || option}
                                        </MenuItem>
                                    ))}
                                </CustomSelect>
                            )}
                        </Grid>
                    ))}

                    {requirePhone && (
                        <Grid item xs={12} md={6}>
                            <TextField
                                label={`Phone number${requirePhone ? '*' : ''}`}
                                placeholder="Enter Phone Number"
                                value={phoneNumber}
                                onChange={(e) => handlePhoneChange(e.target.value)}
                                onBlur={handlePhoneBlur}
                                error={!!phoneValidation}
                                helperText={phoneValidation}
                                inputProps={{ maxLength: 10 }}
                                fullWidth
                                variant="outlined"
                            />
                        </Grid>
                    )}
                </Grid>
            

            
                <Box sx={{ mt: 3, display: 'flex', alignItems: 'start' }}>
                        <CustomCheckbox 
                            label={confirmationCheckboxText}
                            checked={confirm}
                            onChange={(e) => setConfirm(e.target.checked)}
                            sx={{ 
                                '&.MuiCheckbox-root':{
                                    py: 0,
                                }
                            }}
                        />
                        {consentValidation && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {consentValidation}
                            </Typography>
                        )}
                </Box>
                <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <CustomButton
                        variant="filled"
                        onClick={() => {
                            if (showConfirmModal) {
                                if (validateForm()) {
                                    setOpenConfirmModal(true);
                                }
                            } else {
                                handleConfirmClick();
                            }
                        }}
                        disabled={
                            isLoading || 
                            isSubmitting || 
                            (!extractedData || Object.keys(extractedData).length === 0) ||
                            !confirm ||
                            (requirePhone && (!phoneNumber.trim() || !/^\d{10}$/.test(phoneNumber.replace(/\D/g, '')))) ||
                            (requireRelation && !relation)
                        }
                        label={saveButtonText}
                        fullWidth
                    />
                </Box>
                            

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <ReusableModal
                    open={openConfirmModal}
                    onClose={() => {
                        setOpenConfirmModal(false);
                    }}
                    title={confirmModalTitle}
                    maxWidth="800px"
                    scrollable={true}
                >
                    <Box sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                    }}>
                        <Box sx={{ 
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                            {/* Document preview */}
                            {uploadedFile && (
                                <Box sx={{ mb: 3 }}>
                                    {uploadedFile.type === 'application/pdf' ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: '60px',
                                                    height: '60px',
                                                    backgroundColor: '#dc3545',
                                                    borderRadius: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                                                    PDF
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" sx={{ color: '#666' }}>
                                                    PDF Document Uploaded
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#666' }}>
                                                    {uploadedFile.name}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box
                                            component="img"
                                            src={URL.createObjectURL(uploadedFile)}
                                            alt="Uploaded document"
                                            sx={{
                                                maxWidth: '100%',
                                            }}
                                        />
                                    )}
                                </Box>
                            )}

                            {/* Extracted data summary */}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={6} sm={6}>
                                    <Typography variant="labelD" sx={{ color: '#666' }}>
                                        Name
                                    </Typography>
                                    <Typography variant="body1">
                                        {extractedData.full_name}
                                    </Typography>
                                </Grid>

                                {selectedDocumentType !== 'PAN' && (
                                    <Grid item xs={6} sm={6}>
                                        <Typography variant="labelD" sx={{ color: '#666' }}>
                                            Gender
                                        </Typography>
                                        <Typography variant="body1">
                                            {extractedData.gender}
                                        </Typography>
                                    </Grid>
                                )}
                                
                                <Grid item xs={6} sm={6}>
                                    <Typography variant="labelD" sx={{ color: '#666' }}>
                                        Date of Birth
                                    </Typography>
                                    <Typography variant="body1">
                                        {new Date(extractedData.dob).toLocaleDateString('en-GB', { 
                                            day: 'numeric', 
                                            month: 'long', 
                                            year: 'numeric' 
                                        })}
                                    </Typography>
                                </Grid>
                                
                                {extractedData.dob && (
                                    <Grid item xs={6} sm={6}>
                                        <Typography variant="labelD" sx={{ color: '#666' }}>
                                            Age
                                        </Typography>
                                        <Typography 
                                            variant="body1"
                                            sx={{
                                                color: calculateAgesAndFlags([extractedData.dob], minAge, maxAge)[0]?.ageFlag 
                                                    ? 'error.main' : 'inherit'
                                            }}
                                        >
                                            {getAge(extractedData.dob)} years
                                        </Typography>
                                    </Grid>
                                )}
                                
                                <Grid item xs={6} sm={6}>
                                    <Typography variant="labelD" sx={{ color: '#666' }}>
                                        Phone Number
                                    </Typography>
                                    <Typography variant="body1">
                                        {phoneNumber}
                                    </Typography>
                                </Grid>
                                
                                {requireRelation && relation && (
                                    <Grid item xs={6} sm={6}>
                                        <Typography variant="labelD" sx={{ color: '#666' }}>
                                            Relation
                                        </Typography>
                                        <Typography variant="body1">
                                            {relations.find(r => r.value === relation)?.label || relation}
                                        </Typography>
                                    </Grid>
                                )}
                                
                                {/* Additional fields in modal */}
                                {additionalFields.map((field, index) => (
                                    additionalFieldsData[field.name] && (
                                        <Grid item xs={6} sm={6} key={index}>
                                            <Typography variant="labelD" sx={{ color: '#666' }}>
                                                {field.label}
                                            </Typography>
                                            <Typography variant="body1">
                                                {field.type === 'select' 
                                                    ? field.options.find(opt => (opt.value || opt) === additionalFieldsData[field.name])?.label || additionalFieldsData[field.name]
                                                    : additionalFieldsData[field.name]
                                                }
                                            </Typography>
                                        </Grid>
                                    )
                                ))}
                            </Grid> 
                        </Box>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h3">
                                Confirm all details are correct.
                            </Typography>
                            <Typography variant="body1">
                                Are you sure you want to proceed?
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mb: 4}}>
                            <CustomButton
                                variant="outlined"
                                onClick={() => {
                                    setOpenConfirmModal(false);
                                    if (onEditManually) {
                                        onEditManually();
                                    }
                                }}
                                label={editManuallyText}
                                fullWidth
                            />
                            <CustomButton
                                variant="filled"
                                onClick={handleConfirmClick}
                                disabled={isLoading || isSubmitting}
                                loading={isSubmitting}
                                label={confirmText}
                                fullWidth
                            />
                        </Box>
                    </Box>
                </ReusableModal>
            )}
            {owner_type_text && (
                <AgeWarningModal
                    updateRelation={updateRelation}
                    showAgeModal={showAgeModal}
                    close={handleCloseAgeModal}
                    callBloodRelationAPI={callBloodRelationAPI}
                    owner_type_text={owner_type_text}
                />
            )}
        </Box>
    );
};

export default ReusableFileUploadArea;